import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { 
  Search, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  FileText,
  Trash2
} from 'lucide-react';
import { 
  getModuleContent, 
  regenerateQuiz, 
  regeneratePracticeLab,
  getProblematicModules,
  ModuleContentData 
} from '@/services/contentManagement';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ContentReviewPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleContentData | null>(null);
  const [problematicModules, setProblematicModules] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState<'quiz' | 'practice' | null>(null);

  useEffect(() => {
    loadProblematicModules();
  }, []);

  const loadProblematicModules = async () => {
    try {
      setLoading(true);
      const modules = await getProblematicModules();
      setProblematicModules(modules);
    } catch (error) {
      toast.error('Failed to load problematic modules');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewModule = async (moduleId: string) => {
    try {
      const content = await getModuleContent(moduleId);
      setSelectedModule(content);
    } catch (error) {
      toast.error('Failed to load module content');
      console.error(error);
    }
  };

  const handleRegenerateQuiz = async () => {
    if (!selectedModule) return;
    
    try {
      setRegenerating('quiz');
      await regenerateQuiz(selectedModule.moduleId);
      toast.success('Quiz regenerated successfully');
      
      // Refresh module data
      const updated = await getModuleContent(selectedModule.moduleId);
      setSelectedModule(updated);
    } catch (error) {
      toast.error('Failed to regenerate quiz');
      console.error(error);
    } finally {
      setRegenerating(null);
    }
  };

  const handleRegeneratePractice = async () => {
    if (!selectedModule) return;
    
    try {
      setRegenerating('practice');
      await regeneratePracticeLab(selectedModule.moduleId);
      toast.success('Practice lab regenerated successfully');
      
      // Refresh module data
      const updated = await getModuleContent(selectedModule.moduleId);
      setSelectedModule(updated);
    } catch (error) {
      toast.error('Failed to regenerate practice lab');
      console.error(error);
    } finally {
      setRegenerating(null);
    }
  };

  const filteredModules = problematicModules.filter(m =>
    m.moduleTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.moduleId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Content Review & Management</CardTitle>
          <CardDescription>
            Review and fix modules with missing or failed content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              onClick={loadProblematicModules}
              disabled={loading}
              variant="outline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            Found {filteredModules.length} modules with issues
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredModules.map((module) => (
              <div
                key={module.moduleId}
                className="p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                onClick={() => handleViewModule(module.moduleId)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">{module.moduleTitle}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {module.moduleId}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {module.issues.map((issue: string) => (
                      <Badge
                        key={issue}
                        variant={issue === 'missing_content' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {issue.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-2 mt-2 text-xs">
                  {module.hasQuiz ? (
                    <span className="flex items-center text-green-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Quiz
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <XCircle className="h-3 w-3 mr-1" /> No Quiz
                    </span>
                  )}
                  
                  {module.hasPracticeLab ? (
                    <span className="flex items-center text-green-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Practice
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <XCircle className="h-3 w-3 mr-1" /> No Practice
                    </span>
                  )}
                  
                  <span className="text-muted-foreground">
                    {module.contentWordCount} words
                  </span>
                </div>
              </div>
            ))}

            {filteredModules.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-600" />
                <p>No problematic modules found!</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Module Detail Dialog */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedModule?.moduleTitle}</DialogTitle>
            <DialogDescription>{selectedModule?.moduleId}</DialogDescription>
          </DialogHeader>

          {selectedModule && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Content</p>
                  <p className="text-2xl font-bold">{selectedModule.contentWordCount}</p>
                  <p className="text-xs text-muted-foreground">words</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm font-medium">Quiz</p>
                  {selectedModule.hasQuiz ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600" />
                  )}
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm font-medium">Practice Lab</p>
                  {selectedModule.hasPracticeLab ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600" />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Content Preview</label>
                <Textarea
                  value={selectedModule.content.slice(0, 500) + (selectedModule.content.length > 500 ? '...' : '')}
                  readOnly
                  className="min-h-[200px] font-mono text-xs"
                />
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleRegenerateQuiz}
                  disabled={!selectedModule.content || regenerating === 'quiz'}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${regenerating === 'quiz' ? 'animate-spin' : ''}`} />
                  Regenerate Quiz
                </Button>

                <Button
                  onClick={handleRegeneratePractice}
                  disabled={!selectedModule.content || regenerating === 'practice'}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${regenerating === 'practice' ? 'animate-spin' : ''}`} />
                  Regenerate Practice Lab
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
